import intersectionRatio from '../intersection-ratio';
import debounce from 'lodash/debounce';

/**
 * Play/pause videos as they scroll in and out of the viewport.
 * Only allows one video to play at a time, whichever is most visible.
 *
 * ```
 * // To start watching videos, pass a selector, HTMLElement, or NodeList to `watch()`:
 * visibleVideos.watch('video');
 *
 * // To stop watching videos, use `unwatch()`:
 * visibleVideos.unwatch('video');
 * ```
 *
 * @see https://github.com/niklasramo/mezr/
 */
class VisibleVideos {

  constructor() {
    this.videos = []
    //this.handleScroll = debounce(this.handleScroll.bind(this), 250)
    window.addEventListener('optimizedScroll', this.handleScroll.bind(this))
  }

  handleScroll() {
    if (!this.videos.length) { return; }

    let visibilityRatios = {
      standard: .4,
      siteFlythrough: .6,
    }

    // Get visible videos
    let visibleVideos = this.videos
      .map(video => ({ video: video, visibleRatio: intersectionRatio(video) }))
      .filter((video) => {

        let ratioKey = 'standard';
        if (video.video.classList.contains('site-flythrough')) {
          ratioKey = 'siteFlythrough';
        }

        return video.visibleRatio >= visibilityRatios[ratioKey]
      });

    // If there are no visible videos, pause everything
    if (!visibleVideos.length) {
      return this.videos.forEach(video => video.pause());
    }

    // Otherwise, get the most visible video
    let mostVisible = visibleVideos.reduce((most, next) => {
      return (next.visibleRatio > most.visibleRatio) ? next : most;
    });

    // Play it and pause everything else
    this.videos.forEach(video => {

      // Need to skip videos that can't be autoplayed
      if (!video.hasAttribute('autoplay') || video.classList.contains('slideshow-slide')) {
        return;
      }

      if (video === mostVisible.video) {
        if (video.paused) {
          video.play();
        }
        return
      }

      if (!video.paused) {
        video.pause();
      }
    });
  }

  addVideos(newVideos) {
    if (typeof newVideos === 'string') {
      newVideos = Array.from(document.querySelectorAll(newVideos));
    } else if (newVideos instanceof NodeList) {
      newVideos = Array.from(newVideos);
    }

    newVideos = newVideos.filter((video) => !video.classList.contains('js-vimeo-loop'))
    this.videos = this.videos.concat(newVideos);
    this.videos.map((video) => video.pause())

    this.videos.forEach((video) => {
      const videoContainer = video.closest('.js-autoplay-has-audio')
      if (!videoContainer) {
        return
      }

      const muteControlContainer = videoContainer.querySelector('.js-autoplay-mute-control')
      if (!muteControlContainer) {
        return
      }

      if (video.muted) {
        muteControlContainer.classList.add('video-muted')
      } else {
        muteControlContainer.classList.remove('video-muted')
      }

      muteControlContainer.querySelector('.js-autoplay-mute').addEventListener('click', (e) => {
        e.preventDefault()
        video.muted = true
        muteControlContainer.classList.add('video-muted')
      })

      muteControlContainer.querySelector('.js-autoplay-unmute').addEventListener('click', (e) => {
        e.preventDefault()
        video.muted = false
        muteControlContainer.classList.remove('video-muted')
      })
    })

  }

  removeVideos(oldVideos) {
    if (typeof oldVideos === 'string') {
      oldVideos = Array.from(document.querySelectorAll(oldVideos));
    } else if (oldVideos instanceof NodeList) {
      oldVideos = Array.from(oldVideos);
    }

    this.videos = this.videos.filter(video => oldVideos.indexOf(video) < 0);
  }

  clearVideos() {
    this.videos = []
  }
}

export default VisibleVideos
